import React, { useState } from 'react';
import { Lead } from '../types';

interface LeadProfileProps {
  lead: Lead;
  onBack: () => void;
  onAddNote: (leadId: string, text: string) => void;
  onDeleteNote: (leadId: string, noteId: string) => void;
  onValidate: (leadId: string, status: 'Correct' | 'Incorrect', feedback?: string) => void;
  onMarkInterested: (leadId: string) => void;
}

const LeadProfile: React.FC<LeadProfileProps> = ({ lead, onBack, onAddNote, onDeleteNote, onMarkInterested }) => {
  const [noteText, setNoteText] = useState('');

  const handleAddNote = () => {
    if (noteText.trim()) {
      onAddNote(lead.id, noteText);
      setNoteText('');
    }
  };

  const websiteUrl = lead.website !== 'NONE' ? (lead.website.startsWith('http') ? lead.website : `https://${lead.website}`) : null;

  return (
    <div className="max-w-7xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">
      <div className="flex items-center justify-between mb-8">
        <button onClick={onBack} className="bg-white px-5 py-2.5 rounded-xl border border-gray-200 text-gray-500 hover:text-black font-black text-[10px] uppercase tracking-widest flex items-center gap-2 transition-all shadow-sm hover:shadow-md">
          <i className="fa-solid fa-arrow-left"></i> Return to Furnace
        </button>
        <div className="flex gap-3">
           <button onClick={() => onMarkInterested(lead.id)}
            className={`px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-xl flex items-center gap-2 ${lead.isArchived ? 'bg-black text-white hover:bg-gray-800' : 'bg-red-600 text-white hover:bg-red-700 shadow-red-200'}`}>
             <i className={`fa-solid ${lead.isArchived ? 'fa-door-open' : 'fa-vault'}`}></i>
             {lead.isArchived ? 'Unlock to Furnace' : 'Secure in the Vault'}
           </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Details & Insights */}
        <div className="lg:col-span-8 space-y-6">
          <div className="bg-white p-10 rounded-3xl border border-gray-200 shadow-2xl border-t-[12px] border-t-black">
            <div className="flex flex-col md:flex-row justify-between items-start mb-12 gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-6">
                  <span className="bg-red-600 text-white px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest shadow-lg shadow-red-100">{lead.industry}</span>
                  <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest">{lead.province}</span>
                </div>
                <h2 className="text-6xl font-black text-black tracking-tighter uppercase italic leading-[0.85] mb-6">{lead.businessName}</h2>
                <div className="flex items-center gap-4">
                  <div className="text-[11px] font-black uppercase text-gray-400 tracking-widest">Scale: <span className="text-black">{lead.companySize || "Small/Medium Enterprise"}</span></div>
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-200"></div>
                  <div className="text-[11px] font-black uppercase text-gray-400 tracking-widest">Forged: <span className="text-black">{lead.dateScraped}</span></div>
                </div>
              </div>
              <div className="text-center md:text-right bg-black p-8 rounded-[2rem] border border-gray-800 min-w-[180px] shadow-2xl">
                <div className="text-[10px] font-black text-red-600 uppercase tracking-[0.2em] mb-2">Strike Score</div>
                <div className="text-6xl font-black text-white leading-none">{lead.vettingScore}<span className="text-xl text-red-600">.0</span></div>
              </div>
            </div>

            {/* Direct Contact Hub */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
              <a href={`tel:${lead.contactNumber}`} className="flex flex-col items-center p-8 bg-slate-50 border border-slate-100 rounded-3xl group hover:bg-black hover:text-white transition-all shadow-sm">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mb-4 shadow-sm group-hover:bg-red-600 group-hover:text-white transition-all">
                  <i className="fa-solid fa-phone text-xl"></i>
                </div>
                <div className="text-[10px] font-black uppercase tracking-widest mb-1">Call Target</div>
                <div className="text-[11px] font-bold text-gray-400 truncate w-full text-center">{lead.contactNumber || 'N/A'}</div>
              </a>
              <a href={`https://wa.me/${lead.contactNumber?.replace(/\D/g, '')}`} target="_blank" className="flex flex-col items-center p-8 bg-slate-50 border border-slate-100 rounded-3xl group hover:bg-green-600 hover:text-white transition-all shadow-sm">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mb-4 shadow-sm group-hover:bg-green-500 group-hover:text-white transition-all">
                  <i className="fa-brands fa-whatsapp text-2xl"></i>
                </div>
                <div className="text-[10px] font-black uppercase tracking-widest mb-1">WhatsApp Strike</div>
                <div className="text-[11px] font-bold text-gray-400">Direct Message</div>
              </a>
              <a href={`mailto:${lead.email}`} className="flex flex-col items-center p-8 bg-slate-50 border border-slate-100 rounded-3xl group hover:bg-red-600 hover:text-white transition-all shadow-sm">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mb-4 shadow-sm group-hover:bg-red-500 group-hover:text-white transition-all">
                  <i className="fa-solid fa-envelope text-xl"></i>
                </div>
                <div className="text-[10px] font-black uppercase tracking-widest mb-1">Email Batch</div>
                <div className="text-[11px] font-bold text-gray-400 truncate w-full text-center">{lead.email || 'N/A'}</div>
              </a>
            </div>

            <div className="bg-red-50/30 border border-red-100/50 p-8 rounded-3xl">
              <h4 className="text-[12px] font-black text-red-600 uppercase tracking-[0.2em] mb-6 italic flex items-center gap-3">
                <i className="fa-solid fa-microchip animate-pulse"></i> Agent Intelligence
              </h4>
              <p className="text-lg text-slate-800 font-medium leading-relaxed italic border-l-4 border-red-600 pl-8">
                "{lead.notes || 'Intelligence gathering incomplete for this target.'}"
              </p>
            </div>
          </div>

          {/* Activity Log */}
          <div className="bg-white p-10 rounded-3xl border border-gray-200 shadow-xl">
            <h3 className="text-[12px] font-black uppercase tracking-[0.2em] mb-10 border-b-2 border-black pb-2 inline-block italic">Strike Activity Log</h3>
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <textarea 
                value={noteText} 
                onChange={e => setNoteText(e.target.value)} 
                placeholder="Log a strike result, objection, or transformation opportunity..." 
                className="flex-1 bg-slate-50 border border-slate-200 rounded-2xl p-6 text-sm font-medium outline-none focus:border-red-600 focus:ring-4 focus:ring-red-50 transition-all min-h-[120px]" 
              />
              <button onClick={handleAddNote} className="bg-black text-white px-10 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-gray-800 transition-all h-fit self-end sm:self-auto shadow-xl">Commit Note</button>
            </div>
            <div className="space-y-6">
              {lead.activityLog?.length === 0 ? (
                <div className="text-center py-16 opacity-30">
                  <i className="fa-solid fa-pen-nib text-4xl mb-4"></i>
                  <p className="text-[11px] font-black uppercase tracking-widest italic">No sequence history yet.</p>
                </div>
              ) : lead.activityLog?.map(note => (
                <div key={note.id} className="p-6 bg-slate-50 border border-slate-100 rounded-2xl flex justify-between items-start group hover:bg-white hover:shadow-lg transition-all border-l-8 border-l-slate-200">
                  <div className="flex-1">
                    <div className="text-[10px] font-black text-red-600 mb-2 uppercase tracking-widest flex items-center gap-2">
                      <i className="fa-solid fa-clock"></i> {new Date(note.date).toLocaleString()}
                    </div>
                    <p className="text-sm font-bold text-slate-800 leading-relaxed">{note.text}</p>
                  </div>
                  <button onClick={() => onDeleteNote(lead.id, note.id)} className="ml-4 text-slate-200 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all">
                    <i className="fa-solid fa-trash-can"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column: Digital Hub */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-black text-white p-10 rounded-3xl shadow-2xl sticky top-28 overflow-hidden">
            <div className="absolute top-0 right-0 w-48 h-48 bg-red-600/10 rounded-bl-full -mr-24 -mt-24 pointer-events-none"></div>
            <h3 className="text-[12px] font-black uppercase tracking-[0.25em] text-red-600 mb-10 italic flex items-center gap-3">
              <i className="fa-solid fa-fingerprint"></i> Digital Footprint
            </h3>
            
            <div className="space-y-8 relative z-10">
              {/* Website Block */}
              <div className="space-y-3">
                <div className="text-[9px] font-black text-gray-500 uppercase tracking-[0.2em] px-1">Primary Domain</div>
                {websiteUrl ? (
                  <a href={websiteUrl} target="_blank" className="flex items-center justify-between p-5 bg-gray-900/80 rounded-2xl border border-gray-800 hover:border-red-600/50 hover:bg-gray-800 group transition-all">
                    <div className="flex items-center gap-4 truncate">
                      <i className="fa-solid fa-globe text-red-600 text-xl"></i>
                      <span className="text-xs font-black truncate">{lead.website}</span>
                    </div>
                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px] text-gray-700 group-hover:text-red-600 group-hover:-translate-y-1 group-hover:translate-x-1 transition-all"></i>
                  </a>
                ) : (
                  <div className="p-5 bg-gray-900/50 rounded-2xl border border-gray-800 border-dashed text-center">
                    <span className="text-[10px] font-black text-gray-700 uppercase italic">Domain Non-Existent</span>
                  </div>
                )}
              </div>

              {/* Social Stack */}
              <div className="space-y-4">
                <div className="text-[9px] font-black text-gray-500 uppercase tracking-[0.2em] px-1">Social Ecosystem</div>
                <div className="grid grid-cols-1 gap-3">
                  {lead.facebookUrl && (
                    <a href={lead.facebookUrl} target="_blank" className="flex items-center justify-between p-5 bg-gray-900 border border-gray-800 rounded-2xl hover:border-blue-600/50 transition-all group/soc">
                      <div className="flex items-center gap-4">
                        <i className="fa-brands fa-facebook text-blue-600 text-2xl"></i>
                        <span className="text-[11px] font-black uppercase tracking-widest">Facebook</span>
                      </div>
                      <i className="fa-solid fa-chevron-right text-[10px] text-gray-800 group-hover/soc:text-blue-600 group-hover/soc:translate-x-1 transition-all"></i>
                    </a>
                  )}
                  {lead.instagramUrl && (
                    <a href={lead.instagramUrl} target="_blank" className="flex items-center justify-between p-5 bg-gray-900 border border-gray-800 rounded-2xl hover:border-pink-600/50 transition-all group/soc">
                      <div className="flex items-center gap-4">
                        <i className="fa-brands fa-instagram text-pink-600 text-2xl"></i>
                        <span className="text-[11px] font-black uppercase tracking-widest">Instagram</span>
                      </div>
                      <i className="fa-solid fa-chevron-right text-[10px] text-gray-800 group-hover/soc:text-pink-600 group-hover/soc:translate-x-1 transition-all"></i>
                    </a>
                  )}
                  {lead.linkedInUrl && (
                    <a href={lead.linkedInUrl} target="_blank" className="flex items-center justify-between p-5 bg-gray-900 border border-gray-800 rounded-2xl hover:border-blue-800/50 transition-all group/soc">
                      <div className="flex items-center gap-4">
                        <i className="fa-brands fa-linkedin text-blue-800 text-2xl"></i>
                        <span className="text-[11px] font-black uppercase tracking-widest">LinkedIn</span>
                      </div>
                      <i className="fa-solid fa-chevron-right text-[10px] text-gray-800 group-hover/soc:text-blue-800 group-hover/soc:translate-x-1 transition-all"></i>
                    </a>
                  )}
                  {!lead.facebookUrl && !lead.instagramUrl && !lead.linkedInUrl && (
                    <div className="p-8 text-center opacity-30 italic">
                      <i className="fa-solid fa-plug-circle-xmark text-3xl mb-3"></i>
                      <p className="text-[10px] font-black uppercase tracking-widest">No Active Channels Linked</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Verified Origins */}
              {lead.verifiedSources && lead.verifiedSources.length > 0 && (
                <div className="pt-8 border-t border-gray-800">
                  <h4 className="text-[9px] font-black text-gray-600 uppercase tracking-[0.2em] mb-4 px-1">Proof of Life (Sources)</h4>
                  <div className="space-y-2">
                    {lead.verifiedSources.map((s, idx) => (
                      <a key={idx} href={s.uri} target="_blank" className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-900 text-gray-400 hover:text-white transition-all">
                        <i className="fa-solid fa-shield-halved text-[10px] text-red-600"></i>
                        <span className="text-[10px] font-bold truncate">{s.title}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadProfile;